import { Fragment } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import type { Profile } from '@wlb/shared/types';
import './profile.css';

export type ProfileStatKey = 'posts' | 'following' | 'followers' | 'buddies';
/** Posts has no list screen of its own, so only the connection counts open one. */
export type ProfileConnectionStatKey = Exclude<ProfileStatKey, 'posts'>;

interface ProfileStatsBarProps {
  profile: Profile;
  /** Omit to render every count as plain text. */
  onSelect?: (key: ProfileConnectionStatKey) => void;
}

const STAT_KEYS: readonly ProfileStatKey[] = ['posts', 'following', 'followers', 'buddies'];

const COUNTS: Record<ProfileStatKey, (profile: Profile) => number> = {
  posts: profile => profile.postCount ?? 0,
  following: profile => profile.followingCount ?? 0,
  followers: profile => profile.followersCount ?? 0,
  buddies: profile => profile.buddiesCount ?? 0,
};

const ACCESSIBILITY_KEYS: Record<ProfileStatKey, string> = {
  posts: 'profile.accessibility.viewPosts',
  following: 'profile.accessibility.viewFollowing',
  followers: 'profile.accessibility.viewFollowers',
  buddies: 'profile.accessibility.viewBuddies',
};

export function ProfileStatsBar({ profile, onSelect }: ProfileStatsBarProps) {
  const { t } = useTranslation();

  return (
    <div className="profile-stats">
      {STAT_KEYS.map((key, index) => {
        const content = (
          <>
            <span className="profile-stats__value">{COUNTS[key](profile)}</span>
            <span className="profile-stats__label">{t(`profile.infoCard.${key}`)}</span>
          </>
        );

        return (
          <Fragment key={key}>
            {index > 0 && <span className="profile-stats__divider" aria-hidden />}
            {onSelect && key !== 'posts' ? (
              <button
                type="button"
                className="profile-stats__item"
                aria-label={t(ACCESSIBILITY_KEYS[key])}
                onClick={() => onSelect(key)}
              >
                {content}
              </button>
            ) : (
              <div className="profile-stats__item">{content}</div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
